/*
 * Copyright © 2017-2021 Skale Education Inc. - All Rights Reserved
 */

import { gql } from "@apollo/client";

// MEMBERS_PER_PAGE must be large enough to display a scroll bar on any screen resolution, or paging will break
// scrollHasTouchedBottom will always be false if no scrolling happens!
// A larger value here is also good for server performance and user experience.
// (Up to a point, it's an inverted U shaped distribution.)

export const GET_DISTRICT_LEADERBOARD = gql`
  query GetDistrictLeaderboard(
    $getUserDistrictRequest: GetUserDistrictRequest
  ) {
    district(getUserDistrictRequest: $getUserDistrictRequest) {
      name
      isActive
      organization {
        name
      }
      storeCount
      userCount
      activeUserList {
        edges {
          node {
            id
            firstName
            lastName
            primaryEmail
            title
            profileImgLgUrl
            profileImgSmUrl
            currMonthSessionCount
            isActive
          }
        }
      }
    }
  }
`;

export const GET_USER_REGION_INFO = gql`
  query GetUserRegionInfo($getUserRegionRequest: GetUserRegionRequest!) {
    region(getUserRegionRequest: $getUserRegionRequest) {
      id
      name
      districtCount
      storeCount
      userCount
    }
  }
`;

export const GET_USER_DISTRICT_INFO = gql`
  query GetUserDistrictInfo($getUserDistrictRequest: GetUserDistrictRequest!) {
    district(getUserDistrictRequest: $getUserDistrictRequest) {
      id
      name
      storeCount
      userCount
      region {
        id
        name
      }
    }
  }
`;

export const GET_USER_STORE_INFO = gql`
  query GetUserStoreInfo($getUserStoreRequest: GetUserStoreRequest!) {
    store(getUserStoreRequest: $getUserStoreRequest) {
      id
      name
      userCount
      region {
        id
        name
      }
      district {
        id
        name
      }
    }
  }
`;

export const GET_USER_STORE_LIST = gql`
  query GetStoreList {
    stores {
      edges {
        node {
          id
          name
        }
      }
    }
  }
`;

export const GET_USER_REGION_LIST = gql`
  query GetRegionList {
    regions {
      edges {
        node {
          id
          name
        }
      }
    }
  }
`;

export const GET_USER_DISTRICT_LIST = gql`
  query GetDistrictList {
    districts {
      edges {
        node {
          id
          name
        }
      }
    }
  }
`;

export const GET_SKILL_LIST = gql`
  query GetSkillList {
    skills {
      edges {
        node {
          id
          name
          isPublished
          isActive
        }
      }
    }
  }
`;

export const GET_ORGANIZATION = gql`
  query organization($orgCode: String!) {
    organization(orgCode: $orgCode) {
      name
      orgCode
      orgJoinRule
      auth0ConnectionName
      isOpen
    }
  }
`;

export const GET_USERPROXY = gql`
  query userproxy($internalId: String!, $orgCode: String!) {
    userproxy(internalId: $internalId, orgCode: $orgCode) {
      internalId
      email
      title
      name
      firstName
      lastName
      userExists
    }
  }
`;

export const GET_PRIMARY_MEMBER_LIST = gql`
  query GetPrimaryMemberList($primaryMemberUserList: Boolean!) {
    users(primaryMemberUserList: $primaryMemberUserList) {
      edges {
        node {
          id
          organization {
            id
            name
            orgCode
          }
        }
      }
    }
  }
`;

export const GET_STAFF_MEMBER_USER_LIST = gql`
  query GetMemberUserList(
    $getStaffMemberUserListRequest: GetStaffMemberUserListRequest
    $cursor: String
  ) {
    staffUsers(
      first: 50 #MEMBERS_PER_PAGE
      getStaffMemberUserListRequest: $getStaffMemberUserListRequest
      after: $cursor
    ) {
      totalCount
      edges {
        node {
          id
          firstName
          lastName
          title
          internalId
          primaryEmail
          region {
            id
            name
          }
          district {
            id
            name
          }
          store {
            id
            name
          }
          hireDate
          created
          primaryLastLogin
          lastActive
          isAdmin
          isStaff
          isManager
          isTrainer
          isCoach
          profileImgSmUrl
        }
      }
      pageInfo {
        endCursor
        hasNextPage
      }
    }
  }
`;

export const GET_STAFF_MEMBER_USER_SEARCH_LIST = gql`
  query GetMemberUserSearchList(
    $getStaffMemberUserListRequest: GetStaffMemberUserListRequest
    $cursor: String
  ) {
    staffUsers(
      first: 50 #MEMBERS_PER_PAGE
      getStaffMemberUserListRequest: $getStaffMemberUserListRequest
      after: $cursor
    ) {
      totalCount
      edges {
        node {
          id
          firstName
          lastName
          primaryEmail
        }
      }
      pageInfo {
        endCursor
        hasNextPage
      }
    }
  }
`;

export const GET_STAFF_USERPROXIES_LIST = gql`
  query GetUserproxiesList(
    $getStaffUserproxiesListRequest: GetStaffUserproxiesListRequest
    $cursor: String
  ) {
    staffUserproxies(
      first: 50 #MEMBERS_PER_PAGE
      getStaffUserproxiesListRequest: $getStaffUserproxiesListRequest
      after: $cursor
    ) {
      totalCount
      edges {
        node {
          email
          hireDate
          firstName
          lastName
          title
          internalId
          region {
            name
          }
          district {
            name
          }
          store {
            name
          }
        }
      }
      pageInfo {
        endCursor
        hasNextPage
      }
    }
  }
`;

export const GET_STAFF_SESSION_LIST = gql`
  query GetSessionList(
    $getStaffSessionListRequest: GetStaffSessionListRequest!
    $cursor: String
  ) {
    staffSessions(
      first: 20 #SESSIONS_PER_PAGE
      getStaffSessionListRequest: $getStaffSessionListRequest
      after: $cursor
    ) {
      totalCount
      completedCount
      edges {
        node {
          id
          created
          duration
          archiveUrl
          isCancelled
          thumbnailUrl
          teacherRating
          teacherSelfRating
          learnerRating
          learnerSelfRating
          transcriptionUrl
          hasTranscript
          teacherSummit {
            name
          }
          learnerSummit {
            name
          }
          skill {
            id
            name
            titleTeacher
            titleLearner
          }
          learner {
            id
            firstName
            lastName
            profileImgSmUrl
            title
            store {
              name
            }
            cohort {
              name
            }
          }
          teacher {
            id
            firstName
            lastName
            profileImgSmUrl
            title
            store {
              name
            }
            cohort {
              name
            }
          }
        }
      }
      pageInfo {
        endCursor
        hasNextPage
      }
    }
  }
`;

export const GET_USER_SESSION_LIST = gql`
  query GetUserSessionList(
    $first: Int!
    $getUserSessionListRequest: GetUserSessionListRequest
    $cursor: String
  ) {
    userSessions(
      first: $first
      getUserSessionListRequest: $getUserSessionListRequest
      after: $cursor
    ) {
      completedCount
      edges {
        node {
          id
          created
          duration
          isCancelled
          cancelledReason
          thumbnailUrl
          skill {
            id
            name
          }
          learner {
            id
            firstName
            lastName
            title
            profileImgLgUrl
            profileImgSmUrl
          }
          teacher {
            id
            firstName
            lastName
            title
            profileImgLgUrl
            profileImgSmUrl
          }
        }
      }
      pageInfo {
        endCursor
        hasNextPage
      }
    }
  }
`;

export const GET_STAFF_SESSION_SECRET = gql`
  query GetStaffSessionSecret(
    $getStaffSessionSecretRequest: GetStaffSessionSecretRequest!
  ) {
    staffSession(getStaffSessionSecretRequest: $getStaffSessionSecretRequest) {
      id
      created
      organization {
        id
        name
        showProprietaryPopup
      }
      teacher {
        id
        firstName
        lastName
        email
        profileImgSmUrl
        title
        store {
          name
        }
      }
      learner {
        id
        firstName
        lastName
        email
        profileImgSmUrl
        title
        store {
          name
        }
      }
      skill {
        name
        titleTeacher
        titleLearner
      }
      teacherRating
      teacherSelfRating
      learnerRating
      learnerSelfRating
      masteryLevel
      masteryEdited
      allowSharing
      sessionSteps
      stepsReadyTeacher
      stepsReadyLearner
      archiveUrl
      transcriptionUrl
      hasTranscript
      isCancelled
    }
  }
`;

export const GET_STAFF_USER_INFO = gql`
  query GetStaffUserInfo($getStaffUserInfoRequest: GetStaffUserInfoRequest!) {
    staffUser(getStaffUserInfoRequest: $getStaffUserInfoRequest) {
      id
      primaryEmail
      firstName
      lastName
      profileImgSmUrl
      profileImgLgUrl
      region {
        id
        name
      }
      district {
        id
        name
      }
      store {
        id
        name
        leader {
          firstName
          lastName
          profileImgSmUrl
        }
      }
      score
      sessionCount
      hasSeenTutorial
      title
    }
  }
`;

export const GET_SKILL_DETAIL = gql`
  query GetSkillDetail($getSkillDetailRequest: GetSkillDetailRequest!) {
    skill(getSkillDetailRequest: $getSkillDetailRequest) {
      id
      order
      name
      description
      titleTeacher
      titleLearner
      sessionSteps
      videoUrl
      isPublished
      isActive
      skillModule {
        id
        order
        name
      }
      suggestedTotalMinutes
      videoUrl
    }
  }
`;

export const GET_ME_PRIMARY = gql`
  query GetMePrimary {
    user {
      id
      email
      firstName
      lastName
    }
  }
`;

export const GET_ME = gql`
  query GetMe {
    user {
      id
      primaryEmail
      firstName
      lastName
      channelName
      organization {
        id
        name
        domain
        orgCode
        isOpen
        isOpenStaff
        isOpenTrainer
        isOpenMaintenance
        hasTimeClockAlert
        logoUrl
        showDistrict
        showTranscription
        archiveRatio
        startButtonText
        launchDate
        allowTitleEdit
        orgType
        activeQuota {
          startDate
          endDate
          requiredSessionCount
        }
        auth0ConnectionName
        doAgoraForceTcp
        channelName
        managerDashboardEnabled
        cachedFieldsLastUpdated
        userCount
        userproxyCount
      }
      profileImgSmUrl
      profileImgLgUrl
      region {
        id
      }
      district {
        id
      }
      store {
        id
        name
      }
      score
      hasSeenTutorial
      isAdmin
      isManager
      isStaff
      isTrainer
      isCoach
      title
      isReceivingQueueUpdates
      leaderSummary {
        id
        isLeader
        type
      }
      type
      onesignalId
    }
  }
`;

export const GET_PRIMARY_MEMBER_TOKEN = gql`
  query GetPrimaryMemberToken(
    $getPrimaryMemberTokenRequest: GetPrimaryMemberTokenRequest
  ) {
    user(getPrimaryMemberTokenRequest: $getPrimaryMemberTokenRequest) {
      token
    }
  }
`;

export const GET_SKALESESSION = gql`
  query getSkaleSession(
    $getSkaleSessionDetailRequest: GetSkaleSessionDetailRequest!
  ) {
    skalesession(getSkaleSessionDetailRequest: $getSkaleSessionDetailRequest) {
      id
      summit {
        id
      }
      teacher {
        id
        firstName
        lastName
        title
        profileImgSmUrl
        profileImgLgUrl
        organization {
          name
        }
        district {
          name
        }
      }
      learner {
        id
        firstName
        lastName
        title
        profileImgSmUrl
        profileImgLgUrl
        organization {
          name
        }
        district {
          name
        }
      }
      skill {
        id
        name
        titleTeacher
        titleLearner
        description
        sessionSteps
        suggestedTotalMinutes
      }
      isTeacherReady
      isLearnerReady
      hasTeacherSeenContent
      hasLearnerSeenContent
      stepsReadyTeacher
      stepsReadyLearner
      isVendorMatch
      agoraChannel
    }
  }
`;

export const GET_SUMMIT_DETAIL = gql`
  query GetSummitDetail($getSummitRequest: GetSummitRequest!) {
    userSummit(getSummitRequest: $getSummitRequest) {
      id
      isPublished
      name
      inviteUuid
      description
      startDate
      endDate
      imageUrl
      introVideoUrl
      skillModules {
        id
        order
        name
        activeSkillList {
          edges {
            node {
              id
              order
              name
              description
              titleTeacher
              titleLearner
              sessionSteps
              videoUrl
              isPublished
              isActive
              skillModule {
                id
                order
                name
              }
              suggestedTotalMinutes
            }
          }
        }
        publishedSkillList {
          edges {
            node {
              id
              order
              name
              description
              titleTeacher
              titleLearner
              sessionSteps
              videoUrl
              isPublished
              isActive
              skillModule {
                id
                order
                name
              }
              suggestedTotalMinutes
            }
          }
        }
        isActive
      }
      cohorts {
        id
        name
        cohortType
      }
      matchAlgorithm
      organization {
        domain
        orgCode
        name
      }
      timeWindows {
        edges {
          node {
            id
            startTime
            endTime
            text
            order
          }
        }
      }
    }
  }
`;

export const GET_USER_SESSION_QUOTA_COMPLETION = gql`
  query GetUserSessionListQuotaCompletion(
    $getUserSessionListRequest: GetUserSessionListRequest
  ) {
    userSessions(
      first: 0
      getUserSessionListRequest: $getUserSessionListRequest
    ) {
      completedCount
    }
  }
`;

export const GET_STAFF_SUMMIT_LIST = gql`
  query GetStaffSummitList {
    staffSummits {
      totalCount
      edges {
        node {
          id
          isPublished
          name
          inviteUuid
          description
          startDate
          endDate
          imageUrl
          introVideoUrl
          skillModules {
            id
            order
            name
            activeSkillList {
              edges {
                node {
                  id
                  order
                  name
                  description
                  titleTeacher
                  titleLearner
                  sessionSteps
                  videoUrl
                  isPublished
                  isActive
                  skillModule {
                    id
                    order
                    name
                  }
                  suggestedTotalMinutes
                }
              }
            }
            publishedSkillList {
              edges {
                node {
                  id
                  order
                  name
                  description
                  titleTeacher
                  titleLearner
                  sessionSteps
                  videoUrl
                  isPublished
                  isActive
                  skillModule {
                    id
                    order
                    name
                  }
                  suggestedTotalMinutes
                }
              }
            }
            isActive
          }
          matchAlgorithm
          organization {
            domain
            orgCode
            name
          }
        }
      }
    }
  }
`;

export const GET_USER_SUMMIT_SESSION_LIST = gql`
  query GetUserSummitSessionList(
    $getUserSummitSessionListRequest: GetUserSummitSessionListRequest
  ) {
    skalesessions(
      getUserSummitSessionListRequest: $getUserSummitSessionListRequest
    ) {
      edges {
        node {
          id
          learner {
            id
            firstName
            lastName
          }
          teacher {
            id
            firstName
            lastName
          }
          skill {
            id
            name
            suggestedTotalMinutes
            isPublished
          }
          created
          isCancelled
        }
      }
    }
  }
`;

export const SUMMIT_ROUTE_GET_SKILL_DETAIL = gql`
  query SummitRouteGetSkillDetail(
    $getSkillDetailRequest: GetSkillDetailRequest!
  ) {
    skill(getSkillDetailRequest: $getSkillDetailRequest) {
      description
      isPublished
    }
  }
`;

export const GET_CURRICULUM = gql`
  query GetCurriculum {
    staffSkillModules {
      edges {
        node {
          id
          order
          name
          description
          publishedSkillList {
            edges {
              node {
                id
                order
                name
                titleTeacher
              }
            }
          }
          isActive
        }
      }
    }
  }
`;

export const GET_COHORTS = gql`
  query GetCohorts {
    staffCohorts {
      edges {
        node {
          id
          name
          cohortType
        }
      }
    }
  }
`;

export const GET_SUMMIT_FEEDBACK_LIST = gql`
  query GetSummitFeedbackList(
    $getSummitFeedbackRequest: GetSummitFeedbackRequest!
    $cursor: String
  ) {
    staffSummitFeedback(
      first: 15 #SUMMITS_PER_PAGE
      getSummitFeedbackRequest: $getSummitFeedbackRequest
      after: $cursor
    ) {
      totalCount
      pageInfo {
        endCursor
        hasNextPage
      }
      edges {
        node {
          id
          created
          session {
            id
            thumbnailUrl
            archiveUrl
            duration
          }
          summit {
            id
            name
          }
          content
          user {
            id
            firstName
            lastName
            primaryEmail
          }
          role
        }
      }
    }
  }
`;

export const GET_SUMMIT_ATTENDANCE_LIST = gql`
  query GetSummitAttendanceList(
    $getStaffSummitAttendanceListRequest: GetStaffSummitAttendanceListRequest!
    $cursor: String
  ) {
    staffUsers(
      first: 20 #SESSIONS_PER_PAGE
      getStaffSummitAttendanceListRequest: $getStaffSummitAttendanceListRequest
      after: $cursor
    ) {
      totalCount
      edges {
        node {
          id
          firstName
          lastName
          profileImgSmUrl
          summitSessionCount
          title
          primaryEmail
          isTrainer
        }
      }
      pageInfo {
        endCursor
        hasNextPage
      }
    }
  }
`;

export const GET_STAFF_PERIODICTASK_LIST = gql`
  query GetStaffPeriodicTaskList {
    staffPeriodictasks {
      totalCount
      edges {
        node {
          id
          kwargs
          clocked {
            clockedTime
          }
        }
      }
    }
  }
`;

export const GET_CURRICULUM_LIST = gql`
  query GetCurriculumList {
    staffSkillModules {
      edges {
        node {
          id
          order
          name
          description
          isActive
          canBeReviewed
          activeSkillList {
            edges {
              node {
                id
                order
                name
                description
                isPublished
                isActive
              }
            }
          }
        }
      }
    }
  }
`;

export const GET_SKILL_MODULE_DETAIL = gql`
  query GetSkillModuleDetail(
    $getSkillModuleDetailRequest: GetSkillModuleDetailRequest!
  ) {
    skillModule(getSkillModuleDetailRequest: $getSkillModuleDetailRequest) {
      id
      order
      name
      description
      isActive
      canBeReviewed
    }
  }
`;

// TODO should time windows be part of this query? (maybe not)
export const GET_ACTIVE_SUMMIT_LIST = gql`
  query GetActiveSummitList($isStaff: Boolean) {
    userActiveSummits(isStaff: $isStaff) {
      totalCount
      edges {
        node {
          id
          name
          startDate
          endDate
          imageUrl
          skillModules {
            id
          }
          introVideoUrl
          description
          timeWindows {
            edges {
              node {
                id
                startTime
                endTime
                text
                order
              }
            }
          }
        }
      }
    }
  }
`;

export const GET_ACTIVE_SUMMIT_CURRICULUM = gql`
  query GetActiveSummitCurriculum {
    userActiveSummits(isStaff: false) {
      edges {
        node {
          id
          name
          startDate
          endDate
          skillModules {
            id
            order
            name
            publishedSkillList {
              edges {
                node {
                  id
                  order
                  name
                }
              }
            }
          }
        }
      }
    }
  }
`;

// TODO it would be nice to add a parameter to the backend for this query
// to only fetch summits that happened within the current month (we do that a lot)
export const GET_PAST_SUMMIT_CURRICULUM = gql`
  query GetPastSummitCurriculum {
    userPastSummits(first: 3, isStaff: false) {
      edges {
        node {
          id
          name
          startDate
          endDate
          skillModules {
            id
            order
            name
            publishedSkillList {
              edges {
                node {
                  id
                  order
                  name
                }
              }
            }
          }
        }
      }
    }
  }
`;

export const GET_STAFF_ACTIVE_SUMMIT_LIST = gql`
  query GetStaffActiveSummitList($isStaff: Boolean) {
    userActiveSummits(isStaff: $isStaff) {
      totalCount
      edges {
        node {
          id
          name
          startDate
          endDate
          imageUrl
          isPublished
          skillModules {
            id
          }
          cohorts {
            id
            name
            cohortType
            userCount
            userproxyCount
          }
          introVideoUrl
          description
          matchAlgorithm
        }
      }
      pageInfo {
        endCursor
        hasNextPage
      }
    }
  }
`;

export const GET_PAST_SUMMIT_LIST = gql`
  query GetPastSummitList($isStaff: Boolean, $cursor: String) {
    userPastSummits(
      first: 15 #SUMMITS_PER_PAGE
      isStaff: $isStaff
      after: $cursor
    ) {
      totalCount
      edges {
        node {
          id
          name
          startDate
          endDate
          imageUrl
          isPublished
          skillModules {
            id
          }
          introVideoUrl
          description
        }
      }
      pageInfo {
        endCursor
        hasNextPage
      }
    }
  }
`;

export const GET_STAFF_PAST_SUMMIT_LIST = gql`
  query GetStaffPastSummitList($isStaff: Boolean, $cursor: String) {
    userPastSummits(
      first: 8 #SUMMITS_PER_PAGE
      isStaff: $isStaff
      after: $cursor
    ) {
      totalCount
      edges {
        node {
          id
          name
          startDate
          endDate
          imageUrl
          skillModules {
            id
          }
          cohorts {
            id
            name
            cohortType
            userCount
            userproxyCount
          }
          introVideoUrl
          description
          matchAlgorithm
        }
      }
      pageInfo {
        endCursor
        hasNextPage
      }
    }
  }
`;

export const GET_STAFF_COHORTS = gql`
  query GetStaffCohorts($cursor: String) {
    staffCohorts(first: 15, after: $cursor) {
      edges {
        node {
          id
          name
          cohortType
          userCount
          userproxyCount
        }
      }
      pageInfo {
        endCursor
        hasNextPage
      }
    }
  }
`;

export const GET_QUOTA_REPORT = gql`
  query GetQuotaReport($userId: GlobalID) {
    quotaReport(userId: $userId) {
      quotaCompletedSessions
      monthlyCompletedSessions
      activeSummitCompletion {
        totalActiveSkills
        completedSkills
        summit {
          id
          name
        }
      }
      pastSummitCompletion {
        totalActiveSkills
        completedSkills
        summit {
          id
          name
        }
      }
    }
  }
`;

export const GET_STORE_USERS_PARTICIPATION = gql`
  query GetStoreUsersParticipation(
    $getUserStoreRequest: GetUserStoreRequest!
    $first: Int
    $cursor: String
    $isOnTrack: Boolean
  ) {
    store(getUserStoreRequest: $getUserStoreRequest) {
      id
      name
      quotaProgress
      quotaTarget
      isOnTrack
      childrenCompletedQuota
      userCount
      users(isOnTrack: $isOnTrack, first: $first, after: $cursor) {
        edges {
          node {
            id
            firstName
            lastName
            quotaProgress
            quotaTarget
            isOnTrack
          }
        }
        pageInfo {
          endCursor
          hasNextPage
        }
      }
      leader {
        id
        firstName
        lastName
        profileImgSmUrl
        profileImgLgUrl
        title
        hireDate
      }
      district {
        id
        name
      }
      region {
        id
        name
      }
      organization {
        id
        name
      }
    }
  }
`;

export const GET_DISTRICT_STORES_PARTICIPATION = gql`
  query GetDistrictStoresParticipation(
    $getUserDistrictRequest: GetUserDistrictRequest!
    $first: Int
    $cursor: String
    $isOnTrack: Boolean
  ) {
    district(getUserDistrictRequest: $getUserDistrictRequest) {
      id
      name
      quotaProgress
      quotaTarget
      isOnTrack
      childrenCompletedQuota
      storeCount
      stores(isOnTrack: $isOnTrack, first: $first, after: $cursor) {
        edges {
          node {
            id
            name
            quotaProgress
            quotaTarget
            isOnTrack
            participatingUsers
            userCount
          }
        }
        pageInfo {
          endCursor
          hasNextPage
        }
      }
      leader {
        id
        firstName
        lastName
        profileImgSmUrl
        profileImgLgUrl
        title
        hireDate
      }
      region {
        id
        name
      }
      organization {
        id
        name
      }
    }
  }
`;

export const GET_REGION_DISTRICTS_PARTICIPATION = gql`
  query GetRegionDistrictsParticipation(
    $getUserRegionRequest: GetUserRegionRequest!
    $first: Int
    $cursor: String
    $isOnTrack: Boolean
  ) {
    region(getUserRegionRequest: $getUserRegionRequest) {
      id
      name
      quotaProgress
      quotaTarget
      isOnTrack
      childrenCompletedQuota
      districtCount
      districts(isOnTrack: $isOnTrack, first: $first, after: $cursor) {
        edges {
          node {
            id
            name
            quotaProgress
            quotaTarget
            isOnTrack
            participatingUsers
            userCount
          }
        }
        pageInfo {
          endCursor
          hasNextPage
        }
      }
      leader {
        id
        firstName
        lastName
        profileImgSmUrl
        profileImgLgUrl
        title
        hireDate
      }
      organization {
        id
        name
      }
    }
  }
`;

export const GET_ORG_REGIONS_PARTICIPATION = gql`
  query GetOrgRegionsParticipation(
    $first: Int
    $cursor: String
    $isOnTrack: Boolean
  ) {
    user {
      organization {
        id
        name
        quotaProgress
        quotaTarget
        isOnTrack
        childrenCompletedQuota
        regionCount
        regions(isOnTrack: $isOnTrack, first: $first, after: $cursor) {
          edges {
            node {
              id
              name
              quotaProgress
              quotaTarget
              isOnTrack
              participatingUsers
              userCount
            }
          }
          pageInfo {
            endCursor
            hasNextPage
          }
        }
      }
    }
  }
`;

export const GET_ORG_QUOTA_COMPLETION = gql`
  query GetOrgQuotaCompletion {
    user {
      organization {
        quotaProgress
        quotaTarget
      }
    }
  }
`;

export const GET_USER_PARTICIPATION = gql`
  query GetUserParticipation($userId: GlobalID!) {
    user(userId: $userId) {
      id
      name
      firstName
      lastName
      quotaTarget
      quotaProgress
      isOnTrack
      organization {
        id
        name
      }
      district {
        id
        name
      }
      region {
        id
        name
      }
      store {
        id
        name
        leader {
          id
          firstName
          lastName
          profileImgSmUrl
          profileImgLgUrl
          title
          hireDate
        }
      }
    }
  }
`;

export const GET_KUDOS = gql`
  query GetKudos(
    $getKudosRequest: GetKudosRequest2!
    $first: Int
    $cursor: String
  ) {
    kudos2(getKudosRequest: $getKudosRequest, first: $first, after: $cursor) {
      edges {
        node {
          id
          categoryHelpfulTips
          categoryKnowledgeable
          categoryGoodConversation
          categoryFriendly
          message
          created
          sender {
            id
            firstName
            lastName
            profileImgSmUrl
          }
          recipient {
            id
            firstName
            lastName
            profileImgSmUrl
          }
          session {
            id
            thumbnailUrl
            duration
            archiveUrl
          }
          approved
        }
      }
      pageInfo {
        endCursor
        hasNextPage
      }
    }
  }
`;
